import { FC } from 'react';
import { useIntl } from 'react-intl';

import { ChipProps, Stack, Box } from '@mui/material';

import clsx from 'clsx';
import _difference from 'lodash/difference';
import _isArray from 'lodash/isArray';
import _without from 'lodash/without';

import { convertToTaxonomyCase, EVENTS, useTrack } from '@arrived/analytics';
import { OfferingFilterOption, useOfferingFilters } from '@arrived/contexts';
import { OfferingSearchQueryKeys } from '@arrived/models';

import { FilterIcon } from '../PropertyGrid';
import { OfferingFilterChip } from './OfferingFilterChip';

export interface CombinableFilterOptionsProps {
  Component?: FC<ChipProps>;
  /**
   * Objects defining the selectable options for this filter.
   */
  options: Readonly<OfferingFilterOption[]>;

  /**
   * The key of the filter option to be referenced to the internal state
   */
  filterKey: OfferingSearchQueryKeys;
}

/**
 * A component for rendering combinable filter options. It is assumed that one of the provided filterOptions is an
 * "All" option which has no filter definition. In the case that one is applied, the setFilter function is invoked
 * with an undefined value.
 *
 * If all values are deselected then the setFilter function is also invoked with undefined.
 */
export const CombinableFilterOptions = ({ Component, options, filterKey }: CombinableFilterOptionsProps) => {
  const track = useTrack();
  const intl = useIntl();
  const { isFilterActive, setOfferingFilter } = useOfferingFilters();

  return (
    <>
      {options.map(({ disabled, filter, label, icon }, id) => {
        const isActive = isFilterActive(filterKey, filter);

        const handleOnClick = () => {
          setOfferingFilter(filterKey, filter);

          track(EVENTS.OFFERING_FILTER_CLICKED, {
            filter_name: convertToTaxonomyCase(intl.formatMessage(label)),
          });
        };

        const newLabel = (
          <Stack alignItems="center" justifyContent="center" spacing={0.5} textAlign="center" whiteSpace="normal">
            {icon && (
              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                <FilterIcon name={icon} />
              </Box>
            )}
            {intl.formatMessage(label, label.values)}
          </Stack>
        );

        if (Component) {
          return (
            <Component
              className={clsx({ active: isActive })}
              clickable
              disabled={disabled}
              label={newLabel}
              onClick={handleOnClick}
              key={id}
            />
          );
        }

        return (
          <OfferingFilterChip
            className={clsx({ active: isActive })}
            clickable
            disabled={disabled}
            label={newLabel}
            onClick={handleOnClick}
            key={id}
          />
        );
      })}
    </>
  );
};
